import React from 'react';
import { Icon } from "../../../../atoms";
import { Plugin } from "flatpickr/dist/types/options";
import { createRoot } from "react-dom/client";
import { CustomTimePickerConfig } from "../DatePickerBase.types";
import {
  createDropdownElement,
  filterDropdownOptions,
  generateTimeOptions,
  parseTimeInput,
  positionDropdownRelativeToInput,
  toggleDropdownVisibility
} from "./utils";

const dateTimeDropdownPlugin = (config: CustomTimePickerConfig): Plugin => {
  const { minuteStep = 30 } = config;

  return (flatpickrInstance) => {
    let dropdownElement: HTMLDivElement;
    let timeInputField: HTMLInputElement | undefined;
    let wrapperElement: HTMLElement | undefined;
    let preventClose = false;

    const createDropdown = () => {
      dropdownElement = createDropdownElement("time-dropdown");
      dropdownElement.style.position = "absolute";
      dropdownElement.style.display = "none";
    };

    const positionDropdown = () => {
      if (timeInputField && dropdownElement) {
        positionDropdownRelativeToInput(dropdownElement, timeInputField, wrapperElement);
      }
    };

    const closeDropdown = () => {
      toggleDropdownVisibility(dropdownElement!, false);
    };

    const handleTimeOptionClick = (time: string) => {
      // fill the input field with the selected time
      if (timeInputField) {
        timeInputField.value = time;
      }

      // set the date to the selected time
      const selectedDate = flatpickrInstance.selectedDates[0] || new Date();
      // split the time into hour and minute
      const [hour, minute] = time.split(":");
      // check if the time is in PM
      const isPM = time.includes("PM");
      // adjust the hour to the correct format
      const adjustedHour = isPM ? (parseInt(hour, 10) % 12) + 12 : parseInt(hour, 10) % 12;

      // set the date to the selected time
      selectedDate.setHours(adjustedHour, parseInt(minute, 10));
      // set the flatpickr date to the selected time
      flatpickrInstance.setDate(selectedDate, false);

      // close the dropdown and focus the input field
      timeInputField?.focus();
      closeDropdown();
    };

    const handleInputBlur = () => {
      // don't close dropdown if user clicked on the dropdown and didn't select an option
      if (preventClose) {
        preventClose = false;
        return;
      }

      if (timeInputField) {
        const selectedDate = flatpickrInstance.selectedDates[0] || new Date();
        const datePart = flatpickrInstance.formatDate(selectedDate, "m/d/Y");
        // parse time input to HH:MM AM/PM format
        const parsedTime = parseTimeInput(timeInputField.value);

        // format date and time to HH:MM AM/PM format
        const fullDateTimeString = `${datePart} ${parsedTime}`;
        if (fullDateTimeString) {
          try {
            // parse the date and time to a date object
            const parsedDate = new Date(fullDateTimeString);
            flatpickrInstance.setDate(parsedDate, true);
            // fill input field with formatted time
            timeInputField.value = parsedDate.toLocaleTimeString(undefined, {
              hour: "numeric",
              minute: "numeric",
              hour12: true
            }).toUpperCase();
          } catch (err) {
            console.error('Error setting date:', err);
          }
        }
      }

      closeDropdown();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Allow arrow keys, home, end, backspace, delete for cursor movement and editing
      const allowedKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Backspace', 'Delete'];
      if (allowedKeys.includes(e.key)) {
        e.stopPropagation(); // Prevent flatpickr from handling these keys
        return;
      }
    };

    const handleInputChange = (e: Event) => {
      const input = e.target as HTMLInputElement;
      const query = input.value.trim();
      // open dropdown
      toggleDropdownVisibility(dropdownElement!, true);
      // filter dropdown options
      filterDropdownOptions(dropdownElement!, query, generateTimeOptions(minuteStep), handleTimeOptionClick);
      // position dropdown
      positionDropdown();
    };

    const handleInputFocus = () => {
      // open dropdown
      toggleDropdownVisibility(dropdownElement!, true);
      // generate and filter dropdown options
      filterDropdownOptions(
        dropdownElement!,
        timeInputField?.value.trim() || "",
        generateTimeOptions(minuteStep),
        handleTimeOptionClick
      );
      // position dropdown
      positionDropdown();
    };

    return {
      onReady() {
        // create dropdown and wrapper for the input field and dropdown
        const wrapper = document.createElement("div");
        wrapper.className = "custom-time-wrapper";

        // create label for the input field
        const label = document.createElement("label");
        label.className = "custom-time-label";
        label.innerText = "Time";

        // create wrapper for the input
        const inputWrapper = document.createElement("div");
        inputWrapper.className = "custom-time-input-wrapper";
        wrapperElement = inputWrapper;

        // create icon container and render the icon
        const iconContainer = document.createElement("div");
        iconContainer.className = "custom-time-icon";
        const iconRoot = createRoot(iconContainer);
        iconRoot.render(<Icon icon="Schedule" style="Outlined" size="Small" />);

        // create input field 
        const inputField = document.createElement("input");
        inputField.type = "text";
        inputField.className = "custom-time-input";
        // set the initial value of the input field to the selected time
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const initialDate = flatpickrInstance.selectedDates[0] || now;
        inputField.value = initialDate.toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
          hour12: true
        }).toUpperCase();
        inputField.setAttribute("autocomplete", "off");
        // set the time input field to the input field
        timeInputField = inputField;

        // append the icon container and input field to the input wrapper
        inputWrapper.appendChild(iconContainer);
        inputWrapper.appendChild(inputField);
        // append the label and input wrapper to the wrapper
        wrapper.appendChild(label);
        wrapper.appendChild(inputWrapper);

        // create and append dropdown after wrapper is created
        createDropdown();
        inputWrapper.appendChild(dropdownElement);

        // append the wrapper to the calendar container
        flatpickrInstance.calendarContainer.appendChild(wrapper);

        // add event listeners
        inputField.addEventListener("input", handleInputChange);
        inputField.addEventListener("focus", handleInputFocus);
        inputField.addEventListener("keydown", handleKeyDown);
        dropdownElement?.addEventListener("mousedown", () => (preventClose = true));
        inputField.addEventListener("blur", handleInputBlur);

        // position dropdown on resize and scroll
        window.addEventListener("resize", positionDropdown);
        window.addEventListener("scroll", positionDropdown, true);
      },
      onDestroy() {
        // remove event listeners
        window.removeEventListener("resize", positionDropdown);
        window.removeEventListener("scroll", positionDropdown, true);

        if (timeInputField) {
          timeInputField.removeEventListener("keydown", handleKeyDown);
        }

        if (dropdownElement) {
          dropdownElement.remove();
        }
      }
    };
  };
};

export default dateTimeDropdownPlugin;
